import classNames from 'classnames/bind'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { MAX_TEXTAREA_INPUT_LEN } from '../CandidateReview'

import type {
  CandidateFormDecisionKey,
  CandidateFormSelectOption
} from '@percent/partner-dashboard/services/grants/grantsService.types'
import {
  ButtonText,
  DateInput,
  Dropzone,
  Flex,
  FormField,
  MultiSelect,
  Select,
  TextArea,
  TextInput
} from '@percent/lemonade'
import { DOCUMENT_UPLOAD_ACCEPT, MAX_GRANT_DOCUMENT_FILES } from '@percent/domain/grants'
import { useCountries } from '@percent/partner-dashboard/common/hooks/useCountries/useCountries'
import { startOfTodayUtc } from '@percent/partner-dashboard/app/dashboard/grants/utils/utils'
import styles from './ReviewQuestion.module.scss'
import type { ReviewQuestionProps } from './ReviewQuestion.types'

const cx = classNames.bind(styles)

export function ReviewQuestion({ question, value, handleChange, error, touched }: Readonly<ReviewQuestionProps>) {
  const { t } = useTranslation()
  const { countries } = useCountries()
  const { id, type, placeholder, required, label, description, instructions } = question

  const options = useMemo(() => ('options' in question ? question.options : []), [question.id])
  const countryOptions = useMemo(
    () => (countries ?? []).map(({ code, name }) => ({ value: code, label: name })),
    [countries]
  )

  const renderQuestion = () => {
    switch (type) {
      case 'score':
        return (
          <Flex direction="column" gap={8} flex={1}>
            <Flex gap={8} justify="space-between" data-testid={`${id}-score-buttons`}>
              {Array.from({ length: question.maxScore }, (_, i) => {
                const score = i + 1

                return (
                  <ButtonText
                    key={`${id}-${score}`}
                    className={cx({
                      scoreButton: true,
                      active: value === score
                    })}
                    onClick={() => handleChange(id, score)}
                    data-testid={`q-${id}-btn-score-${score}`}
                  >
                    {score}
                  </ButtonText>
                )
              })}
            </Flex>
          </Flex>
        )
      case 'textarea':
        return (
          <Flex direction="column" w="100%">
            <TextArea
              name={id}
              placeholder={placeholder}
              value={(value as string) ?? ''}
              onChange={e => handleChange(id, e.target.value)}
              data-testid={`q-${id}-text-area`}
              maxLength={'maxLength' in question ? question.maxLength : MAX_TEXTAREA_INPUT_LEN}
            />
          </Flex>
        )
      case 'select':
        if (question.multiple) {
          return (
            <MultiSelect
              name={id}
              placeholder={placeholder}
              defaultValue={options.filter(
                option => Array.isArray(value) && (value as string[]).includes(option.value)
              )}
              onChange={(selected: CandidateFormSelectOption[]) =>
                handleChange(
                  id,
                  selected.map(s => s.value)
                )
              }
              options={options}
              searchable
              data-testid={`q-${id}-multiselect`}
            />
          )
        }

        return (
          <Select
            name={id}
            placeholder={placeholder}
            defaultValue={options.find(option => option.value === value)}
            onChange={e => handleChange(id, e.value)}
            options={options}
            searchable
            data-testid={`q-${id}-${type}`}
          />
        )
      case 'award_recommendation':
        return (
          <Select
            name={id}
            placeholder={placeholder}
            defaultValue={options.find(option => option.value === value)}
            onChange={e => handleChange(id, e.value)}
            options={options}
            searchable
            data-testid={`q-${id}-${type}`}
          />
        )
      case 'country_search':
        return (
          <Select
            name={id}
            placeholder={placeholder}
            onChange={e => handleChange(id, e.value)}
            options={countryOptions}
            searchable
            data-testid={`q-${id}-country-search`}
          />
        )
      case 'decision':
        return (
          <Flex gap={8} flex={1}>
            {(['fail', 'pass'] as CandidateFormDecisionKey[]).map(key => {
              const option = question.options.find(o => o.key === key)

              return (
                <ButtonText
                  key={key}
                  onClick={() => handleChange(id, option?.value ?? '')}
                  className={cx({
                    decisionButton: true,
                    [key]: true,
                    active: value === option?.value
                  })}
                  data-testid={`q-${id}-decision-${key}`}
                >
                  {option?.label}
                </ButtonText>
              )
            })}
          </Flex>
        )
      case 'documents':
        return (
          <Flex direction="column" w="100%">
            <Dropzone
              title={t('grants.candidateForm.review.dropzone.title')}
              description={t('grants.candidateForm.review.dropzone.description', {
                maxFiles: MAX_GRANT_DOCUMENT_FILES
              })}
              files={(value as File[]) || []}
              onChange={files => handleChange(id, files)}
              accept={DOCUMENT_UPLOAD_ACCEPT}
              maxFiles={MAX_GRANT_DOCUMENT_FILES}
              testId={`q-${id}-dropzone`}
            />
          </Flex>
        )
      case 'date':
      case 'scheduled_event':
      case 'payment_date':
        return (
          <Flex direction="column" w="100%">
            <DateInput
              name={id}
              value={value ? new Date(value as string) : null}
              clearable
              utc
              onChange={date => handleChange(id, date ? date.toISOString() : '')}
              placeholder={placeholder}
              data-testid={`q-${id}-date-input`}
              status={error && touched ? 'danger' : undefined}
              maxDate={type === 'payment_date' ? new Date() : undefined}
              minDate={type === 'scheduled_event' ? startOfTodayUtc() : undefined}
            />
          </Flex>
        )
      case 'award_amount':
      case 'final_award_amount':
        return (
          <Flex direction="column" w="100%">
            <TextInput
              name={id}
              placeholder={placeholder}
              value={(value as string) ?? ''}
              onChange={e => handleChange(id, e.target.value)}
              type="number"
              data-testid={`q-${id}-award-amount`}
            />
          </Flex>
        )
      case 'number':
        return (
          <Flex direction="column" w="100%">
            <TextInput
              name={id}
              placeholder={placeholder}
              value={(value as string) ?? ''}
              onChange={e => handleChange(id, e.target.value)}
              type="number"
              data-testid={`q-${id}-number-input`}
              status={error && touched ? 'danger' : undefined}
            />
          </Flex>
        )
      default:
        return (
          <Flex direction="column" w="100%">
            <TextInput
              name={id}
              placeholder={placeholder}
              value={(value as string) ?? ''}
              onChange={e => handleChange(id, e.target.value)}
              data-testid={`q-${id}-text-input`}
              status={error && touched ? 'danger' : undefined}
            />
          </Flex>
        )
    }
  }

  return (
    <Flex direction="column" gap={16}>
      <FormField
        label={label}
        necessity={required ? 'required' : 'optional'}
        status={error && touched ? 'danger' : undefined}
        statusMessage={error && touched ? error : undefined}
        description={description}
        instructions={instructions}
      >
        <Flex flex={1} w="100%">
          {renderQuestion()}
        </Flex>
      </FormField>
    </Flex>
  )
}
